<script setup lang="ts">
type ModalProps = {
  backgroundColor?: string;
  backdropOnly?: boolean;
};

const props = withDefaults(defineProps<ModalProps>(), {
  backgroundColor: 'black',
  backdropOnly: false,
});

const emit = defineEmits(['close-modal']);
const slots = useSlots();
</script>

<template>
  <div class="modal-backdrop" :class="props.backgroundColor">
    <template v-if="!props.backdropOnly">
      <div class="the-modal" role="alertdialog" aria-modal="true" aria-labelledby="modalTitle">
        <div class="close-modal-button">
          <UiCloseButton aria-label="Close panel" @click="emit('close-modal')" />
        </div>
        <div v-if="!!$slots.footer" class="modal-header">
          <h1 id="modalTitle">
            <slot name="title" />
          </h1>
        </div>
        <section class="modal-body">
          <slot name="content" />
        </section>
        <div v-if="slots.footer" class="modal-footer">
          <slot name="footer" />
        </div>
      </div>
    </template>
    <template v-else>
      <div class="backdrop-only">
        <UiCloseButton aria-label="Close panel" @click="emit('close-modal')" />
        <slot />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@use '@/assets/scss/queries';
.modal-backdrop {
  align-items: center;
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 10000;

  &.black {
    background-color: rgba(0, 0, 0, 0.8);
  }

  &.white {
    background-color: rgb(255, 255, 255);
  }

  .the-modal {
    background: #ffffff;
    box-shadow: 2px 2px 20px 1px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
    gap: 2rem;
    margin: 2rem;
    max-height: 96vh;
    max-width: var(--max-view-width);
    overflow-x: auto;
    overflow-y: auto;
    padding: 2rem;
    position: relative;

    @include queries.for-size(phone-only) {
      margin: 1rem !important;
      padding: 3rem 2rem;
    }

    .close-modal-button {
      position: absolute;
      right: 10px;
      top: 10px;
      z-index: 1;
    }

    .modal-header {
      display: absolute;
      padding-top: 1rem;
      position: relative;

      @include queries.for-size(phone-only) {
        padding-bottom: 2rem;
        padding-top: 2rem;
      }

      h1 {
        font-size: 4.5rem;
        margin: 0;
        text-align: center;

        @include queries.for-size(phone-only) {
          font-size: 3.7rem;
          letter-spacing: 0.15px;
          text-align: left;
        }
      }
    }

    .modal-body {
      display: flex;
      justify-content: center;
    }

    .modal-footer {
      display: flex;
      justify-content: center;
    }
  }

  .backdrop-only {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    margin: auto;
    max-width: 1400px;
    width: auto;
    width: 100%;

    button {
      cursor: pointer;
      margin-left: auto;
    }
  }
}
</style>
